/**
 * Trilingual UI strings for stories, placements, awards, and ad-break
 * handoffs (legacy `frontend/`). Mirrors namespaces under
 * `Stories` / `Awards` / `AdBreak` in `frontend-next/messages/*.json`.
 *
 * After the stories v2 retirement pass:
 *   - The legacy `StoryPlaybackModal` / `StoryViewer` were deleted, so
 *     playback-only keys (`storyPlaybackLoading`, `storyPlaybackEmpty`,
 *     `previousStory`, `nextStory`, `openContent`, `close`) were removed.
 *   - The legacy in-page composer form was removed, so composer-form-only
 *     keys (`storyComposerPlacementType/Story/Profile`,
 *     `storyComposerTextOverlay`, `storyComposerSubmit`,
 *     `storyComposerSuccess`, `storyComposerError`,
 *     `storyComposerLogin`, `signInCta`) were removed.
 *   - Orphaned share-menu keys (`copyLink`, `shareTo`, `shareVia`) were
 *     removed — the live consumers in `ShareActions` use `shareUi.ts`.
 *   - `storyComposerTitle` + `storyComposerInvalidEntity` are retained
 *     because the iframe bridge pages still surface those strings.
 *   - `storiesTemporarilyUnavailable` + `backHome` are new, used by the
 *     bridge pages when `stories_v2` is rolled back.
 */
export type StoryLocale = "en-US" | "ja-JP" | "ko-KR";

const STRINGS: Record<
  StoryLocale,
  {
    addToStory: string;
    resteep: string;
    resteepModalTitle: string;
    resteepToTarget: string;
    resteepSuccess: string;
    resteepError: string;
    resteepLogin: string;
    resteepSelectDestination: string;
    resteepIssueSearchPlaceholder: string;
    resteepProfileOption: string;
    resteepOrSeparator: string;
    resteepAddThoughts: string;
    resteepCreatingIssue: string;
    resteepIssueCreated: string;
    resteepSelectIssue: string;
    resteepWaitIssue: string;
    storyComposerTitle: string;
    storyComposerInvalidEntity: string;
    storyPlaybackTitle: string;
    viewStory: string;
    repliedToStoryLabel: string;
    storiesTemporarilyUnavailable: string;
    backHome: string;
    awardButton: string;
    awardCount: string;
    awardGiven: string;
    awardInsufficient: string;
    awardLogin: string;
    awardError: string;
    awardBalanceLabel: string;
    awardBalanceTitle: string;
    awardGivingBudgetLabel: string;
    awardGivingBudgetTitle: string;
    reputationReceivedLabel: string;
    reputationReceivedTitle: string;
    promotionCreditsLabel: string;
    promotionCreditsTitle: string;
    awardZeroBalanceTitle: string;
    awardZeroBalanceSubtitle: string;
    awardStartAdBreak: string;
    awardViewAllowanceDetails: string;
    awardConfirmGiveTitle: string;
    awardConfirmGiveBody: string;
    awardConfirmGiveConfirm: string;
    awardConfirmGiveCancel: string;
    navAdBreak: string;
    adBreakLinkTitle: string;
    promoteOnAdBreaks: string;
    promoteProfileOnAdBreaks: string;
    promoteParteaOnAdBreaks: string;
    promoteMenuTitle: string;
    profileMoreActionsLabel: string;
    promoteModalTitle: string;
    promoteModalSubtitle: string;
    promoteStakeLabel: string;
    promoteImpressionsPreview: string;
    promoteExtendExisting: string;
    promoteConfirmButton: string;
    promoteCancelButton: string;
    promoteBalanceLabel: string;
    promoteTeamSelectLabel: string;
    promoteInsufficientBalance: string;
    promoteSuccess: string;
    promoteError: string;
    promoteNotEligibleNotPublic: string;
    promoteNotEligibleSettingOff: string;
    promoteNotEligiblePrivate: string;
    promoteDisabled: string;
    promotedBadge: string;
    communityBadge: string;
    sponsoredBadge: string;
    allowPromotionToggleLabel: string;
    allowPromotionToggleDescription: string;
    adBreakPreferenceLabel: string;
    adBreakPreferencePromoted: string;
    adBreakPreferenceAds: string;
    adBreakPreferenceMixed: string;
    adBreakPreferenceAdsComingSoon: string;
    adBreakConsentPreferenceLabel: string;
    adBreakConsentPreferenceDescription: string;
    adBreakConsentNonPersonalized: string;
    adBreakConsentPersonalized: string;
    adBreakConsentSaved: string;
    adBreakConsentError: string;
  }
> = {
  "en-US": {
    addToStory: "Add to Story",
    resteep: "Resteep",
    resteepModalTitle: "Resteep",
    resteepToTarget: "Resteep to {target}",
    resteepSuccess: "Successfully resteeped!",
    resteepError: "Failed to create resteep. Please try again.",
    resteepLogin: "You must be logged in to resteep.",
    resteepSelectDestination: "Please select a destination for your resteep.",
    resteepIssueSearchPlaceholder: "Issue to resteep to\u2026",
    resteepProfileOption: "Your profile",
    resteepOrSeparator: "OR",
    resteepAddThoughts: "Add your thoughts\u2026",
    resteepCreatingIssue: "Creating this issue from Wikipedia\u2026",
    resteepIssueCreated: "Issue created (ID: {id}).",
    resteepSelectIssue: "Select an issue above to continue.",
    resteepWaitIssue: "Creating the issue from Wikipedia\u2026 please wait a moment.",
    storyComposerTitle: "Add to your story",
    storyComposerInvalidEntity:
      "This content cannot be added to a story from here. Use Add to Story from a supported post.",
    storyPlaybackTitle: "Story",
    viewStory: "View story",
    repliedToStoryLabel: "Replied to {owner}'s story",
    storiesTemporarilyUnavailable:
      "Stories are temporarily unavailable. Please try again in a moment.",
    backHome: "Back to home",
    awardButton: "Award",
    awardCount: "{count} awards",
    awardGiven: "Award sent",
    awardInsufficient: "Not enough awards",
    awardLogin: "Sign in to award",
    awardError: "Could not send award",
    awardBalanceLabel: "Award balance",
    awardBalanceTitle: "Award balance: {count}",
    awardGivingBudgetLabel: "Awards to give",
    awardGivingBudgetTitle: "Awards to give this week: {count}",
    reputationReceivedLabel: "Reputation received",
    reputationReceivedTitle: "Reputation received: {count}",
    promotionCreditsLabel: "Promotion credits",
    promotionCreditsTitle: "Promotion credits: {count}",
    awardZeroBalanceTitle: "You are out of awards to give this week",
    awardZeroBalanceSubtitle: "Your weekly award-giving budget refreshes as your trust tier grows.",
    awardStartAdBreak: "Start ad break",
    awardViewAllowanceDetails: "View allowance details",
    awardConfirmGiveTitle: "Give an award?",
    awardConfirmGiveBody: "Give 1 award to {target}. This cannot be undone.",
    awardConfirmGiveConfirm: "Give award",
    awardConfirmGiveCancel: "Cancel",
    navAdBreak: "Ad break",
    adBreakLinkTitle: "Open voluntary ad break (opens in web app)",
    promoteOnAdBreaks: "Promote on ad breaks",
    promoteProfileOnAdBreaks: "Promote my profile on ad breaks",
    promoteParteaOnAdBreaks: "Promote this Partea on ad breaks",
    promoteMenuTitle: "More actions",
    profileMoreActionsLabel: "More profile actions",
    promoteModalTitle: "Promote on ad breaks",
    promoteModalSubtitle:
      "Spend promotion credits to feature this on ad breaks. 1 credit \u2248 10 impressions.",
    promoteStakeLabel: "Promotion credits to spend",
    promoteImpressionsPreview: "\u2248 {count} impressions",
    promoteExtendExisting:
      "{remaining} impressions remaining on the current campaign. Your stake will be added.",
    promoteConfirmButton: "Promote for {count} credit(s)",
    promoteCancelButton: "Cancel",
    promoteBalanceLabel: "Promotion credits: {count}",
    promoteTeamSelectLabel: "Promote as",
    promoteInsufficientBalance: "You don't have enough promotion credits.",
    promoteSuccess: "Promotion campaign started",
    promoteError: "Could not start promotion. Please try again.",
    promoteNotEligibleNotPublic: "Only public or anonymous content can be promoted.",
    promoteNotEligibleSettingOff:
      "Promotion is disabled in your settings. Enable it under Settings \u2192 Privacy.",
    promoteNotEligiblePrivate: "Private profiles cannot be promoted.",
    promoteDisabled: "Promoted content is not enabled in this environment yet.",
    promotedBadge: "Promoted",
    communityBadge: "Community",
    sponsoredBadge: "Sponsored",
    allowPromotionToggleLabel: "Allow my public content to be promoted on ad breaks",
    allowPromotionToggleDescription:
      "When on, posts or comments you author with public or anonymous visibility can appear in ad breaks once awarded or promoted.",
    adBreakPreferenceLabel: "Ad break content preference",
    adBreakPreferencePromoted: "Promoted content only",
    adBreakPreferenceAds: "Ads only",
    adBreakPreferenceMixed: "Mix of ads and promoted",
    adBreakPreferenceAdsComingSoon: "Ads are coming soon.",
    adBreakConsentPreferenceLabel: "Ad personalization",
    adBreakConsentPreferenceDescription:
      "Choose the ad experience used at the start of ad breaks. Signed-in users only see the choice again if they change it here.",
    adBreakConsentNonPersonalized: "Non-personalized ads",
    adBreakConsentPersonalized: "Personalized ads",
    adBreakConsentSaved: "Ad break preference saved",
    adBreakConsentError: "Could not save ad break preference",
  },
  "ja-JP": {
    addToStory: "ストーリーに追加",
    resteep: "Resteep",
    resteepModalTitle: "Resteep",
    resteepToTarget: "{target}にResteep",
    resteepSuccess: "Resteepしました！",
    resteepError: "Resteepの作成に失敗しました。もう一度お試しください。",
    resteepLogin: "Resteepするにはログインが必要です。",
    resteepSelectDestination: "Resteep先を選択してください。",
    resteepIssueSearchPlaceholder: "Resteep先のイシュー\u2026",
    resteepProfileOption: "自分のプロフィール",
    resteepOrSeparator: "または",
    resteepAddThoughts: "コメントを追加\u2026",
    resteepCreatingIssue: "Wikipediaからイシューを作成中\u2026",
    resteepIssueCreated: "イシュー作成済み（ID: {id}）",
    resteepSelectIssue: "上からイシューを選択してください。",
    resteepWaitIssue: "Wikipediaからイシューを作成中\u2026 少しお待ちください。",
    storyComposerTitle: "ストーリーに追加",
    storyComposerInvalidEntity:
      "ここからはストーリーに追加できません。対応する投稿のメニューから追加してください。",
    storyPlaybackTitle: "ストーリー",
    viewStory: "ストーリーを見る",
    repliedToStoryLabel: "{owner} さんのストーリーに返信",
    storiesTemporarilyUnavailable:
      "ストーリーは一時的に利用できません。しばらくしてからお試しください。",
    backHome: "ホームに戻る",
    awardButton: "アワード",
    awardCount: "アワード {count} 件",
    awardGiven: "アワードを送りました",
    awardInsufficient: "アワードが不足しています",
    awardLogin: "アワードするにはサインインしてください",
    awardError: "送信できませんでした",
    awardBalanceLabel: "アワード残高",
    awardBalanceTitle: "アワード残高: {count}",
    awardGivingBudgetLabel: "贈れるアワード",
    awardGivingBudgetTitle: "今週贈れるアワード: {count}",
    reputationReceivedLabel: "受け取った評価",
    reputationReceivedTitle: "受け取った評価: {count}",
    promotionCreditsLabel: "プロモーションクレジット",
    promotionCreditsTitle: "プロモーションクレジット: {count}",
    awardZeroBalanceTitle: "今週贈れるアワードがありません",
    awardZeroBalanceSubtitle: "信頼度が上がると、毎週のアワード付与枠が増えます。",
    awardStartAdBreak: "広告ブレイクを開始",
    awardViewAllowanceDetails: "付与枠の詳細を見る",
    awardConfirmGiveTitle: "アワードを贈りますか？",
    awardConfirmGiveBody: "{target} に1アワードを贈ります。この操作は取り消せません。",
    awardConfirmGiveConfirm: "アワードを贈る",
    awardConfirmGiveCancel: "キャンセル",
    navAdBreak: "広告ブレイク",
    adBreakLinkTitle: "任意の広告ブレイクを開く（ウェブアプリ）",
    promoteOnAdBreaks: "広告ブレイクでプロモーション",
    promoteProfileOnAdBreaks: "自分のプロフィールを広告ブレイクでプロモーション",
    promoteParteaOnAdBreaks: "このパルティーを広告ブレイクでプロモーション",
    promoteMenuTitle: "その他の操作",
    profileMoreActionsLabel: "プロフィールのその他の操作",
    promoteModalTitle: "広告ブレイクでプロモーション",
    promoteModalSubtitle:
      "プロモーションクレジットを使って広告ブレイクに掲載します。1クレジット \u2248 10インプレッション。",
    promoteStakeLabel: "使うプロモーションクレジット数",
    promoteImpressionsPreview: "\u2248 {count} インプレッション",
    promoteExtendExisting:
      "現在のキャンペーンは残り {remaining} インプレッションです。このステークが追加されます。",
    promoteConfirmButton: "{count} クレジットでプロモーション",
    promoteCancelButton: "キャンセル",
    promoteBalanceLabel: "プロモーションクレジット: {count}",
    promoteTeamSelectLabel: "プロモーションの主体",
    promoteInsufficientBalance: "プロモーションクレジットが不足しています。",
    promoteSuccess: "プロモーションを開始しました",
    promoteError: "プロモーションを開始できませんでした。",
    promoteNotEligibleNotPublic: "公開または匿名のコンテンツのみプロモーションできます。",
    promoteNotEligibleSettingOff:
      "プロモーションは設定で無効になっています。「設定 \u2192 プライバシー」から有効化してください。",
    promoteNotEligiblePrivate: "非公開のプロフィールはプロモーションできません。",
    promoteDisabled: "プロモーションコンテンツはまだ利用できません。",
    promotedBadge: "プロモーション",
    communityBadge: "コミュニティ",
    sponsoredBadge: "広告",
    allowPromotionToggleLabel: "自分の公開コンテンツを広告ブレイクでプロモーションすることを許可",
    allowPromotionToggleDescription:
      "オンにすると、公開または匿名で作成した投稿やコメントが、アワードまたはプロモーションを受けた際に広告ブレイクに表示されます。",
    adBreakPreferenceLabel: "広告ブレイクのコンテンツ設定",
    adBreakPreferencePromoted: "プロモーションのみ",
    adBreakPreferenceAds: "広告のみ",
    adBreakPreferenceMixed: "広告とプロモーションを混合",
    adBreakPreferenceAdsComingSoon: "広告は近日公開予定です。",
    adBreakConsentPreferenceLabel: "広告のパーソナライズ",
    adBreakConsentPreferenceDescription:
      "広告ブレイクの最初に使う広告体験を選択します。サインイン中は、ここで変更しない限り再確認されません。",
    adBreakConsentNonPersonalized: "パーソナライズされない広告",
    adBreakConsentPersonalized: "パーソナライズされた広告",
    adBreakConsentSaved: "広告ブレイク設定を保存しました",
    adBreakConsentError: "広告ブレイク設定を保存できませんでした",
  },
  "ko-KR": {
    addToStory: "스토리에 추가",
    resteep: "Resteep",
    resteepModalTitle: "Resteep",
    resteepToTarget: "{target}에 Resteep",
    resteepSuccess: "Resteep 완료!",
    resteepError: "Resteep 생성에 실패했습니다. 다시 시도해 주세요.",
    resteepLogin: "Resteep하려면 로그인이 필요합니다.",
    resteepSelectDestination: "Resteep 대상을 선택하세요.",
    resteepIssueSearchPlaceholder: "Resteep할 이슈\u2026",
    resteepProfileOption: "내 프로필",
    resteepOrSeparator: "또는",
    resteepAddThoughts: "의견을 추가하세요\u2026",
    resteepCreatingIssue: "Wikipedia에서 이슈 생성 중\u2026",
    resteepIssueCreated: "이슈 생성 완료 (ID: {id})",
    resteepSelectIssue: "위에서 이슈를 선택하세요.",
    resteepWaitIssue: "Wikipedia에서 이슈를 생성 중입니다\u2026 잠시만 기다려 주세요.",
    storyComposerTitle: "내 스토리에 추가",
    storyComposerInvalidEntity:
      "여기서는 스토리에 추가할 수 없습니다. 지원되는 게시물에서 추가하세요.",
    storyPlaybackTitle: "스토리",
    viewStory: "스토리 보기",
    repliedToStoryLabel: "{owner}님의 스토리에 답장",
    storiesTemporarilyUnavailable:
      "스토리를 일시적으로 사용할 수 없습니다. 잠시 후 다시 시도해 주세요.",
    backHome: "홈으로 돌아가기",
    awardButton: "어워드",
    awardCount: "어워드 {count}개",
    awardGiven: "어워드를 보냈습니다",
    awardInsufficient: "어워드가 부족합니다",
    awardLogin: "어워드하려면 로그인하세요",
    awardError: "보낼 수 없습니다",
    awardBalanceLabel: "어워드 잔액",
    awardBalanceTitle: "어워드 잔액: {count}",
    awardGivingBudgetLabel: "이번 주 보낼 어워드",
    awardGivingBudgetTitle: "이번 주 보낼 어워드: {count}",
    reputationReceivedLabel: "받은 평판",
    reputationReceivedTitle: "받은 평판: {count}",
    promotionCreditsLabel: "홍보 크레딧",
    promotionCreditsTitle: "홍보 크레딧: {count}",
    awardZeroBalanceTitle: "이번 주 보낼 어워드가 없습니다",
    awardZeroBalanceSubtitle: "신뢰 등급이 올라가면 매주 어워드 예산이 늘어납니다.",
    awardStartAdBreak: "광고 브레이크 시작",
    awardViewAllowanceDetails: "허용량 세부정보 보기",
    awardConfirmGiveTitle: "어워드를 보내시겠어요?",
    awardConfirmGiveBody: "{target}에 어워드 1개를 보냅니다. 이 작업은 취소할 수 없습니다.",
    awardConfirmGiveConfirm: "어워드 보내기",
    awardConfirmGiveCancel: "취소",
    navAdBreak: "광고 브레이크",
    adBreakLinkTitle: "자발적 광고 브레이크 열기(웹 앱)",
    promoteOnAdBreaks: "광고 브레이크에 홍보",
    promoteProfileOnAdBreaks: "내 프로필을 광고 브레이크에 홍보",
    promoteParteaOnAdBreaks: "이 파르티를 광고 브레이크에 홍보",
    promoteMenuTitle: "추가 작업",
    profileMoreActionsLabel: "프로필 추가 작업",
    promoteModalTitle: "광고 브레이크에 홍보",
    promoteModalSubtitle:
      "홍보 크레딧을 사용해 광고 브레이크에 노출하세요. 1크레딧 \u2248 10회 노출.",
    promoteStakeLabel: "사용할 홍보 크레딧",
    promoteImpressionsPreview: "\u2248 {count}회 노출",
    promoteExtendExisting:
      "현재 캠페인에 {remaining}회 노출이 남아 있습니다. 스테이크가 추가됩니다.",
    promoteConfirmButton: "{count}크레딧으로 홍보",
    promoteCancelButton: "취소",
    promoteBalanceLabel: "홍보 크레딧: {count}",
    promoteTeamSelectLabel: "홍보 주체",
    promoteInsufficientBalance: "홍보 크레딧이 부족합니다.",
    promoteSuccess: "홍보 캠페인을 시작했습니다",
    promoteError: "홍보를 시작할 수 없습니다.",
    promoteNotEligibleNotPublic: "공개 또는 익명 콘텐츠만 홍보할 수 있습니다.",
    promoteNotEligibleSettingOff:
      "설정에서 홍보가 꺼져 있습니다. 설정 \u2192 개인정보에서 활성화하세요.",
    promoteNotEligiblePrivate: "비공개 프로필은 홍보할 수 없습니다.",
    promoteDisabled: "홍보 콘텐츠는 아직 사용할 수 없습니다.",
    promotedBadge: "홍보",
    communityBadge: "커뮤니티",
    sponsoredBadge: "광고",
    allowPromotionToggleLabel: "내 공개 콘텐츠를 광고 브레이크에 홍보 허용",
    allowPromotionToggleDescription:
      "켜면 내가 작성한 공개 또는 익명 게시물/댓글이 어워드를 받거나 홍보될 때 광고 브레이크에 표시됩니다.",
    adBreakPreferenceLabel: "광고 브레이크 콘텐츠 환경설정",
    adBreakPreferencePromoted: "홍보 콘텐츠만",
    adBreakPreferenceAds: "광고만",
    adBreakPreferenceMixed: "광고와 홍보 혼합",
    adBreakPreferenceAdsComingSoon: "광고는 곧 제공됩니다.",
    adBreakConsentPreferenceLabel: "광고 개인화",
    adBreakConsentPreferenceDescription:
      "광고 브레이크 시작 시 사용할 광고 경험을 선택하세요. 로그인한 사용자는 여기서 변경하지 않는 한 다시 묻지 않습니다.",
    adBreakConsentNonPersonalized: "비개인화 광고",
    adBreakConsentPersonalized: "개인화 광고",
    adBreakConsentSaved: "광고 브레이크 설정을 저장했습니다",
    adBreakConsentError: "광고 브레이크 설정을 저장할 수 없습니다",
  },
};

/**
 * Resolves a supported locale from the browser.
 */
export function resolveStoryLocale(): StoryLocale {
  if (typeof navigator === "undefined" || !navigator.language) {
    return "en-US";
  }
  const lang = navigator.language.toLowerCase();
  if (lang.startsWith("ja")) {
    return "ja-JP";
  }
  if (lang.startsWith("ko")) {
    return "ko-KR";
  }
  return "en-US";
}

export function getStoryUiStrings(locale?: StoryLocale): (typeof STRINGS)["en-US"] {
  const key = locale ?? resolveStoryLocale();
  return STRINGS[key] ?? STRINGS["en-US"];
}
